/**
 * Pure cone-placement math. Used by:
 *  - `src/entities/Port.ts` to place the cone sprite inside a Port container
 *    whose origin is the porthole center.
 *  - `src/entities/Organ.ts` (`Organ.buildPreview`) to place cones inside a
 *    composite container whose origin is the organ center.
 *
 * Keep this module free of Phaser imports so it stays trivially testable and
 * importable from anywhere.
 */

import type { Direction } from '../types';

/** Default porthole radius (the grey gauge disc) in pixels. */
export const PORTHOLE_RADIUS = 40;

/** Default cone length (from wide-end to tip) in pixels. */
export const CONE_LEN = 44;

export type PortKind = 'spout' | 'receptacle';

/**
 * Cone position + rotation RELATIVE TO THE PORTHOLE CENTER, suitable for a cone
 * sprite whose origin is (0, 0.5) — i.e. the wide-end center.
 *
 * - SPOUT: wide-end at the porthole, tip pointing OUTWARD in `facing` direction.
 * - RECEPTACLE: tip at the porthole, wide-end ("mouth") faces OUTWARD.
 */
export function coneOffsetFromPorthole(
  kind: PortKind,
  facing: Direction,
  portholeRadius: number = PORTHOLE_RADIUS,
  coneLen: number = CONE_LEN,
): { x: number; y: number; angle: number } {
  const r = portholeRadius;
  if (kind === 'spout') {
    switch (facing) {
      case 'E':
        return { x: r, y: 0, angle: 0 };
      case 'W':
        return { x: -r, y: 0, angle: 180 };
      case 'S':
        return { x: 0, y: r, angle: 90 };
      case 'N':
        return { x: 0, y: -r, angle: 270 };
    }
  }
  const out = r + coneLen;
  switch (facing) {
    case 'E':
      return { x: out, y: 0, angle: 180 };
    case 'W':
      return { x: -out, y: 0, angle: 0 };
    case 'S':
      return { x: 0, y: out, angle: 270 };
    case 'N':
      return { x: 0, y: -out, angle: 90 };
  }
}

/**
 * Offset from the connection-cell center to the porthole center, in world
 * deltas. The porthole is `PORTHOLE_RADIUS + CONE_LEN` away from the cell in
 * the OPPOSITE of `facing` (so the cone tip reaches the cell). Used by
 * Organ previews where the porthole hides behind the organ body.
 */
export function portholeOffsetFromCell(
  facing: Direction,
  portholeRadius: number = PORTHOLE_RADIUS,
  coneLen: number = CONE_LEN,
): { dx: number; dy: number } {
  const d = portholeRadius + coneLen;
  switch (facing) {
    case 'E':
      return { dx: -d, dy: 0 };
    case 'W':
      return { dx: d, dy: 0 };
    case 'S':
      return { dx: 0, dy: -d };
    case 'N':
      return { dx: 0, dy: d };
  }
}
